import React, { useEffect, useState } from "react";
import TableauService from "../../services/TableauService";
import ClientService from "../../services/ClientService";
import CurrentClientContext from "../../providers/CurrentClientProvider";
import { Box, Button, Grid, List, ListItem, ListItemText } from "@mui/material";
import TableauReport from "../../components/tableau/TableauReport";
import { useParams } from "react-router-dom";
import { trackEvent } from "../../services/AmplitudeService";
import MonthSelector from "../../tailwindComponents/ui/dates/month-selector.component";
import EpochMonthService from "../../services/EpochMonth.service";
import { ClassPerformanceReport } from "../../components/reports/classPerformance/ClassPerformanceReport.component";

interface Project {
    id: string;
    name: string;
    workbooks: Workbook[];
}

interface Workbook {
    id: string;
    name: string;
    views: { contentUrl: string }[];
    project: Project;
}

interface TableauReportState {
    url: string;
    clientId: string;
    epochMonth: number;
}

interface Month {
    year: number;
    month: number;
}

const todayEpochMonth = EpochMonthService.getTodayEpochMonth();

function ClientAnalytics() {
    const currentClient = CurrentClientContext.useClient() as any;
    let { clientId } = useParams<{ clientId: string }>();
    const [projects, setProjects] = useState<Project[]>([]);
    const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
    const [tableauReport, setTableauReport] = useState<TableauReportState | null>(null);
    const [lastMonthDataFileFinalized, setLastMonthDataFileFinalized] = useState<boolean>(false);
    const [epochMonth, setEpochMonth] = useState<number>(todayEpochMonth);
    const date = new Date();
    const [currentMonth, setCurrentMonth] = useState<Month>({ year: date.getFullYear(), month: date.getMonth() + 1 });
    const [usingTableau, setUsingTableau] = useState<boolean>(true);

    useEffect(() => {
        document.title = 'Retail ORBIT: Client Analytics';
    }, []);

    useEffect(() => {
        async function loadData() {
            if (currentClient.client) {
                await getProjects();

                // get last month data finalized status
                let _lastMonthDataFileFinalized = await ClientService.getLastMonthDataFileFinalized(currentClient.client.Id);

                if (_lastMonthDataFileFinalized === null) {
                    _lastMonthDataFileFinalized = false;
                } else {
                    _lastMonthDataFileFinalized = _lastMonthDataFileFinalized.last_month_data_file_finalized;
                }

                setLastMonthDataFileFinalized(_lastMonthDataFileFinalized);
            }
        }

        loadData();
    }, [currentClient.client]);

    function filterExports(workbooks: Workbook[]): Workbook[] {
        // filters workbooks from export project based on faves and shared custom flags set in profile
        return workbooks.filter(wb => !wb.name.toLowerCase().includes('mrf') &&
            !wb.name.toLowerCase().includes('goals') &&
            !wb.name.toLowerCase().includes('faves') &&
            !wb.name.toLowerCase().includes('mke'));
    }

    async function getProjects() {
        let filter1 = '+';
        let filter2 = 'live';
        let projectSort: Record<string, number> = {
            'Analytics': 0,
            'Progress': 1,
            'Take Action': 2,
            'Plan': 3,
            'My Reports': 4,
            'Export': 5,
        };
        let _projects = await TableauService.getProjects(clientId);

        _projects.sort(
            (a: any, b: any) => (projectSort[a.name] || 0) - (projectSort[b.name] || 0)
        );
        setProjects(_projects);
        if (_projects.length > 0) {
            let _p = structuredClone(_projects);
            let _f = filterProjects(_p, filter1, filter2);
            setFilteredProjects(_f);
        }
    }

    function filterProjects(p: Project[], filter1: string, filter2: string): Project[] {
        let analyticsWorkbookSort: Record<string, number> = {
            'Global Analytics': 0,
            'Buyer Performance': 1,
            'Class Performance': 2,
            'Inventory Performance': 3
        };
        let takeActionWorkbookSort: Record<string, number> = {
            'Inventory Action': 0,
            'Open To Buy Recap': 1,
            'Inventory Variance': 2
        };
        p.forEach((project) => {
            if (project.name.toLowerCase().includes('export')) {
                project.workbooks = filterExports(project.workbooks);
            } else if (project.name.toLowerCase().includes('progress') && (filter2 === 'live' || filter2 === '+')) {
                project.workbooks =
                    project.workbooks.filter(data => !data.name.toLowerCase().includes(filter1));
            } else {
                project.workbooks =
                    project.workbooks.filter(data => !data.name.toLowerCase().includes(filter1)
                        && !data.name.toLowerCase().includes(filter2));
            }
            if (project.name === 'Analytics') {
                project.workbooks.sort(
                    (a, b) =>
                        (analyticsWorkbookSort[a.name] || 0) - (analyticsWorkbookSort[b.name] || 0)
                );
            } else if (project.name === 'Take Action') {
                project.workbooks.sort(
                    (a, b) =>
                        (takeActionWorkbookSort[a.name] || 0) - (takeActionWorkbookSort[b.name] || 0)
                );
            }
        });
        return p;
    }

    function getEpochMonthFromDate(date: Date, noNegativeOne: boolean = false): number {
        const epochDate = new Date('01 January 1970 00:00');

        if (!noNegativeOne) {
            // if date is current month, return -1
            let currentDate = new Date();
            if (date.getFullYear() === currentDate.getFullYear() && date.getMonth() === currentDate.getMonth()) {
                return -1;
            }
        }

        return (date.getMonth() - epochDate.getMonth() + 12 *
            (date.getFullYear() - epochDate.getFullYear()));
    }

    function pickWorkbook(item: Workbook) {
        let _epochMonth = epochMonth;
        if (_epochMonth == todayEpochMonth && !(item.project.name === clientId)) {
            // if current month, set epoch month to -1, except for custom reports
            _epochMonth = -1;
        }

        // if workbook item is in project.name == clientId (i.e My Reports), track different event
        let _projectName = item.project.name === clientId ? 'My Reports' : item.project.name;

        if (_projectName == 'My Reports') {
            trackEvent('Generated Client Custom Analytics Report', {
                generatedFrom: 'Tableau',
                reportName: item.name,
                epochMonth: _epochMonth,
                clientId: clientId,
                clientName: currentClient.client?.Name,
                clientCode: currentClient.client?.Code,
            });
        } else {
            trackEvent('Generated Analytics Report', {
                generatedFrom: 'Tableau',
                reportName: item.name,
                epochMonth: _epochMonth,
                clientId: clientId,
                clientName: currentClient.client?.Name,
                clientCode: currentClient.client?.Code,
            });
        }

        setTableauReport({
            url: item.views[0].contentUrl + '?:toolbar=top&:alerts=n&:subscriptions=n',
            clientId: clientId!,
            epochMonth: _epochMonth
        });
    }

    function disableTableau() {
        setTableauReport(null);
    }

    return <Box sx={{ mt: 1 }}>
        <Grid container sx={{ backgroundColor: '#fff', minHeight: '300px' }}>
            <Grid item lg={12}>
                <MonthSelector epochMonth={epochMonth} setEpochMonth={setEpochMonth} minMonth={todayEpochMonth - 48} maxMonth={todayEpochMonth + 1} />
                {tableauReport && <Button sx={jss.return_button} variant="contained" onClick={disableTableau}>Return to library</Button>}
            </Grid>
            {!tableauReport ? <>
                {filteredProjects.map(project => {
                    const projectName = project.name === clientId ? "My Reports" : project.name;
                    return (
                        <Grid item sx={jss.box_styles} lg={12 / projects.length} key={project.id}>
                            <h1 className="text-lg uppercase text-center bg-[rgb(145,112,173)] px-5 py-2 text-white">
                                {projectName}
                            </h1>
                            <List sx={{ width: '100%' }}>
                                {project.workbooks.map((value) => (
                                    <ListItem
                                        key={value.id}
                                        disableGutters
                                        sx={jss.list_styles}
                                        onClick={() => pickWorkbook(value)}
                                    >
                                        <ListItemText sx={jss.list_item} primary={value.name} />
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    );
                })}
                <Grid item sx={jss.box_styles} lg={12} key={'analytics-reports-v2'}>
                    <h1 className="text-lg uppercase text-center bg-[rgb(145,112,173)] px-5 py-2 text-white">
                        Analytics Reports V2
                    </h1>
                    <List sx={{ width: '100%' }}>
                        <ListItem
                            key={'class-performance-v2'}
                            disableGutters
                            sx={jss.list_styles}
                            onClick={() => {
                                setUsingTableau(false);
                                setTableauReport({
                                    url: 'fakeurlfortesting',
                                    clientId: clientId!,
                                    epochMonth: epochMonth
                                });
                            }}
                        >
                            <ListItemText sx={jss.list_item} primary={"Class Performance v2"} />
                        </ListItem>
                    </List>
                </Grid>
            </> : (usingTableau ?
                <Grid item lg={12}>
                    <TableauReport url={tableauReport.url} clientId={clientId!} epochMonth={tableauReport.epochMonth} />
                </Grid>
                : <Grid item lg={12}>
                    <ClassPerformanceReport clientId={clientId!} epochMonth={tableauReport.epochMonth} />
                </Grid>)
            }
        </Grid>
    </Box>
}

const jss = {
    head_title: {
        fontSize: 18,
        textTransform: 'uppercase',
        textAlign: 'center',
        backgroundColor: 'rgb(145, 112, 173)',
        padding: '10px 20px',
        color: '#fff'
    },
    box_styles: {
        padding: '0 15px',
        boxSizing: 'border-box'
    },
    list_styles: {
        padding: '5px 15px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'gainsboro'
        }
    },
    list_item: {},
    date_box: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginLeft: '15px',
        marginTop: '15px',
        fontSize: '20px',
        ico: {
            margin: '0 5px 0 15px'
        },
        underscore_item: {
            borderBottom: '1px solid #000',
            cursor: 'pointer',
            fontSize: '14px'
        }
    },
    return_button: {
        textTransform: 'uppercase',
        margin: '5px 0 15px 15px'
    }
};

export default ClientAnalytics;